import { gql } from '@apollo/client'
import { IS_VERBOSE } from 'config/development'
import dayjs from 'dayjs'
import { BigNumber, formatUnits } from 'utils/ethersImport'
import { USD_DECIMALS } from 'rfx/lib/legacy'
import { getSyntheticsGraphClient } from 'rfx/lib/subgraph'
import useSWR from 'swr'

type DailyCumulativeStats = {
  name: string
} & Record<string, string | number>

export const getFormattedData = (value: string) => {
  return parseFloat(formatUnits(BigNumber.from(value) || 0, USD_DECIMALS))
}

const createDataForChart = (
  dataInArr: Record<string, any>,
  nameKey: string,
  valueKey: string,
  keyNameForDaily: string,
  keyNameForCumulative: string,
  secondValueKey?: string,
  secondKeyNameForDaily?: string,
  secondKeyNameForCumulative?: string,
) => {
  let vol = 0
  let secondVol = 0
  return dataInArr.reduce(
    (acc: DailyCumulativeStats[], cur: { [x: string]: number }) => {
      const item: DailyCumulativeStats = {
        name: dayjs(cur[nameKey] * 1000).format('MMM D, YYYY hA'),
        [keyNameForDaily]: cur[valueKey],
        [keyNameForCumulative]: vol + cur[valueKey],
      }

      if (
        secondValueKey &&
        secondKeyNameForDaily &&
        secondKeyNameForCumulative
      ) {
        item[secondKeyNameForDaily] = cur[secondValueKey]
        item[secondKeyNameForCumulative] = secondVol + cur[secondValueKey]
      }

      vol += cur[valueKey]
      if (secondValueKey) {
        secondVol += cur[secondValueKey]
      }
      acc.push(item)

      return acc
    },
    [] as DailyCumulativeStats[],
  )
}

type LiquidationStats = {
  timestamp: number
  liquidationVolume: string
}

export function useVolumeStats(
  chainId: number,
  p: {
    period: string
  },
) {
  const { period } = p
  const client = getSyntheticsGraphClient(chainId)

  const key = chainId && client ? [chainId, 'useVolumeStats', period] : null

  const { data, error } = useSWR(key, {
    fetcher: async () => {
      try {
        const _period = period!.toLowerCase()
        const query = gql(`{
        volumeInfos(
            orderBy: timestamp,
            orderDirection: desc,
            ${`where: { period: "${_period}", timestamp_gt: 1731738600  }`}
        ) {
            timestamp
            volumeUsd: marginVolumeUsd
            swapVolumeUsd
          }
          userStats(
            orderBy: timestamp,
            orderDirection: desc,
            ${`where: { period: "${_period}" }`},
            first: 1000
        ) {
          timestamp
          uniqueUsers
          activeUsers
          }
          positionFeesInfoWithPeriods(
            orderBy: id,
            orderDirection: desc,
            ${`where: { period: "${_period}" }`}
        ) {
          id
          totalPositionFeeUsd
          }
          swapFeesInfoWithPeriods(orderBy: id,
            orderDirection: desc,
            where: {period: "${_period}"}){
            id
            totalFeeUsdForPool
          }
          liquidationStats(
            orderBy: timestamp,
            orderDirection: desc,
            ${`where: { period: "${_period}" }`}
        ) {
          timestamp
          liquidationVolume
          }

          globalStats(
          orderBy: timestamp,
          orderDirection: desc,
          ${`where: { period: "${_period}", timestamp_gt: 1731738600 }`}
        ) {
            TVLUsd
            id
            pnlUsd
            period
            timestamp
          }
      }`)

        const { data } = await client!.query({ query, fetchPolicy: 'no-cache' })

        const res = data.volumeInfos.map((item: any) => {
          return {
            timestamp: item.timestamp,
            volumeUsd:
              getFormattedData(item.volumeUsd) +
              getFormattedData(item.swapVolumeUsd),
          }
        })

        const liquidations = data.liquidationStats.map(
          (item: LiquidationStats) => {
            return {
              timestamp: item.timestamp,
              liquidationVolume: getFormattedData(item.liquidationVolume),
            }
          },
        )

        const tvl = data['globalStats'].map((item: any) => {
          return {
            timestamp: item.timestamp,
            TVLUsd: getFormattedData(item.TVLUsd),
          }
        })

        const tradersPNL = data['globalStats'].map((item: any) => {
          return {
            timestamp: item.timestamp,
            profit: getFormattedData(item.pnlUsd),
          }
        })

        const feesArr: Record<string, number> = {}
        ;[
          (data.positionFeesInfoWithPeriods.reverse(),
          data.swapFeesInfoWithPeriods.reverse()),
        ].forEach((arr, idx) => {
          const keyToAccessData =
            idx === 0 ? 'totalFeeUsdForPool' : 'totalPositionFeeUsd'
          arr.reduce(
            (
              acc: { [x: string]: number },
              cur: { [x: string]: string; id: any },
            ) => {
              const timestamp = cur.id

              const fees = getFormattedData(cur[keyToAccessData])

              if (!acc[timestamp]) {
                acc[timestamp] = fees
              } else {
                acc[timestamp] += fees
              }
              return acc
            },
            feesArr,
          )
        })

        const protocolFeesStatsObj = Object.entries(feesArr).map(
          ([timestamp, fees]) => ({
            timestamp,
            fees,
          }),
        )

        return {
          tradingVolume: createDataForChart(
            res.reverse(),
            'timestamp',
            'volumeUsd',
            'Daily Volume',
            'Cumulative Volume',
          ),
          userStats: createDataForChart(
            data.userStats.reverse(),
            'timestamp',
            'activeUsers',
            'Daily Users',
            'Cumulative Users',
          ),
          activeUserStats: createDataForChart(
            data.userStats,
            'timestamp',
            'activeUsers',
            'Daily Active Users',
            'Cumulative Active Users',
          ),
          protocolFeesStats: createDataForChart(
            protocolFeesStatsObj,
            'timestamp',
            'fees',
            'Daily Fees',
            'Cumulative Fees',
          ),
          liquidationVolume: createDataForChart(
            liquidations.reverse(),
            'timestamp',
            'liquidationVolume',
            'Liquidations',
            'Cumulative Liquidations',
          ),
          tvl: createDataForChart(
            tvl.reverse(),
            'timestamp',
            'TVLUsd',
            'TVL',
            'Cumulative TVL',
          ),
          tradersPNL: createDataForChart(
            tradersPNL.reverse(),
            'timestamp',
            'profit',
            'PnL',
            'Cumulative PnL',
          ),
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        IS_VERBOSE && console.log('🚀 ~ fetcher: ~ err:', err)
        return {
          tradingVolume: [],
          userStats: [],
          activeUserStats: [],
          protocolFeesStats: [],
          liquidationVolume: [],
          tvl: [],
          tradersPNL: [],
        }
      }
    },
    revalidateOnFocus: false,
    revalidateOnMount: true,
    revalidateOnReconnect: false,
    refreshWhenOffline: false,
    refreshWhenHidden: false,
    refreshInterval: 0,
  })

  const isLoading = !error && !data

  return {
    data: {
      tradingVolume: data?.tradingVolume || [],
      userStats: data?.userStats || [],
      activeUserStats: data?.activeUserStats || [],
      protocolFeesStats: data?.protocolFeesStats || [],
      liquidationVolume: data?.liquidationVolume || [],
      tvl: data?.tvl || [],
      tradersPNL: data?.tradersPNL || [],
    },
    isLoading,
  }
}
